import RecruitingInterview from './containers/RecruitingInterview';
import { useEditJob } from '../../../hooks/useEditJob';
import Stepper from '../../../components/Stepper';
import JobDetail from './containers/JobDetail';
import styles from './style.module.scss';
import EditMultipleChoiceExamDetail from './containers/EditMultipleChoiceExamDetail';
import { selectEditMultipleChoiceQuestionManuallyModal, selectRegenerateMultipleChoiceQuestionByAIModal } from '../../../store/workspace/selectors';
import { useDispatch, useSelector } from '../../../store/store';
import RegenerateByAI from './components/RegenerateByAI';
import EditManually from './components/EditManually';
import ExamDeadline from './containers/ExamDeadline';
import { useEffect, useState } from 'react';
import { MultipleChoiceQuestionDetail } from '../../../store/workspace/types';
import { editExamThunk, getJobDetailThunk } from '../../../store/workspace/thunks';
import EditCodingExamDetail from './containers/EditCodingExamDetail';
import { toDatePickerFormat } from '../../../utils/dateHelpers';
import Review from './containers/Review';
import Loading from '../../../components/Loading';

function EditJob() {
    const {
        step,
        city,
        state,
        files,
        cities,
        states,
        country,
        jobType,
        posting,
        jobTitle,
        countries,
        deadline,
        stepCount,
        salaryAmount,
        workplaceType,
        jobDescription,
        salaryFrequency,
        takingMultipleChoiceExam,
        setTakingMultipleChoiceExam,
        codingExamLanguages,
        fetchingJobDescription,
        multipleChoiceExamSkills,
        multipleChoiceCodingExam,
        multipleChoiceCodingExamLanguages,
        submit,
        setCity,
        setStep,
        setState,
        setCountry,
        setJobType,
        fileSelect,
        removeFile,
        setJobTitle,
        setDeadline,
        setCityAndState,
        setSalaryAmount,
        setWorkplaceType,
        setJobDescription,
        setSalaryFrequency,
        takingRecruitingInterview,
        setTakingRecruitingInterview,
        getJobDescriptionFromAI,
        setMultipleChoiceExamSkills,
        setMultipleChoiceCodingExam,
        setMultipleChoiceCodingExamLanguages,
        selectedInterviewQuestions,
        setSelectedInterviewQuestions,
        defaultInterviewQuestions,
        setDefaultInterviewQuestions,
        resetInterviewToDefault,
        resumeMatching,
        setResumeMatching,
        extractFileTextErrorModal,
        submittingJobModal,

        isLoading,
        setIsLoading,
        jobId,
    } = useEditJob();

    const dispatch = useDispatch();
    const [isFetching, setIsFetching] = useState(false);
    const [showEditList, setShowEditList] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const editMultipleChoiceQuestionManuallyModal = useSelector(selectEditMultipleChoiceQuestionManuallyModal);
    const regenerateMultipleChoiceQuestionByAIModal = useSelector(selectRegenerateMultipleChoiceQuestionByAIModal);
    const [multipleChoiceExamQuestions, setMultipleChoiceExamQuestions] = useState<MultipleChoiceQuestionDetail[]>([]);
    const [selectedMultipleChoiceQuestion, setSelectedMultipleChoiceQuestion] = useState<MultipleChoiceQuestionDetail | null>(null);
    const [multipleChoiceExamQuestionsDefault, setMultipleChoiceExamQuestionsDefault] = useState<MultipleChoiceQuestionDetail[]>([]);

    const [jobDetail, setJobDetail] = useState<any>(null);
    const [recruitingInterviewRaw, setRecruitingInterviewRaw] = useState<any>(null);
    const [recruitingInterviewRawEdited, setRecruitingInterviewRawEdited] = useState<any>(null);

    useEffect(() => {
        if (jobId) {
            setIsFetching(true);
            dispatch(getJobDetailThunk({
                job: jobId,
            }))
                .unwrap()
                .then((response) => {
                    setJobTitle(response?.title);
                    setJobDescription(response?.description);
                    setSalaryAmount(response?.baseSalaryAmount);
                    setSalaryFrequency(response?.baseSalaryFrequency);
                    setWorkplaceType(response?.workplaceType);
                    setJobType(response?.jobType);
                    setCity(response?.city);
                    setState(response?.state);
                    setCountry(response?.country);
                    setDeadline(toDatePickerFormat(response?.multipleChoiceExamDeadline));
                    setJobDetail(response);
                })
                .catch((err) => {
                    console.log(err);
                })
                .finally(() => {
                    setIsFetching(false);
                });
        }
    }, [jobId]);

    useEffect(() => {
        if (jobId) {
            dispatch(editExamThunk({
                action: 'multiple',
                jobId,
                exam: null
            }))
                .unwrap()
                .then((res) => {
                    setMultipleChoiceExamQuestionsDefault(res?.examInfo?.questions || []);
                    setMultipleChoiceExamQuestions(res?.examInfo?.questions_edited || res?.examInfo?.questions || []);
                })
                .catch((err) => {
                    console.log(err);
                });
        }
    }, [jobId]);

    useEffect(() => {
        if (jobId) {
            dispatch(editExamThunk({
                action: 'recruiting',
                jobId,
                exam: null
            }))
                .unwrap()
                .then((res) => {
                    setSelectedInterviewQuestions(res?.examInfo?.questions_edited || res?.examInfo?.questions || []);
                    setRecruitingInterviewRaw(res?.examInfo?.questions || null);
                    setRecruitingInterviewRawEdited(res?.examInfo?.questions_edited || null);
                })
                .catch((err) => {
                    console.log(err);
                });
        }
    }, [jobId]);

    const resetInterviewToDefaultNew = () => {
        setSelectedInterviewQuestions(recruitingInterviewRawEdited || recruitingInterviewRaw || null);
    }

    const saveJobDetailStep = () => {
        if (!jobDetail) return;

        if (jobDetail?.recruitingInterview) {
            setStep('RecruitingInterview');
        } else if (jobDetail?.multipleChoiceExam) {
            setStep('MultipleChoiceExamDetail');
        } else if (jobDetail?.codingExam) {
            setStep('CodingExamDetail');
        } else {
            setStep('ExamDeadline');
        }
    };

    const postEditedRecruitingInterview = () => {
        if (!jobId) return;

        const body = {
            action: 'recruiting',
            jobId,
            exam: {
                questions: selectedInterviewQuestions,
            }
        };

        setIsLoading(true);
        dispatch(editExamThunk(body))
            .unwrap()
            .then((res) => {
                if (jobDetail?.multipleChoiceExam) {
                    setStep('MultipleChoiceExamDetail');
                } else if (jobDetail?.codingExam) {
                    setStep('CodingExamDetail');
                } else {
                    setStep('ExamDeadline');
                }
            })
            .catch((err) => {
                console.log(err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const postEditedMultipleChoiceExam = () => {
        if (!jobId) return;

        const body = {
            action: 'multiple',
            jobId,
            exam: {
                questions: multipleChoiceExamQuestions
            }
        };

        setIsLoading(true);
        dispatch(editExamThunk(body))
            .unwrap()
            .then((res) => {
                if (jobDetail?.codingExam) {
                    setStep('CodingExamDetail');
                } else {
                    setStep('ExamDeadline');
                }
            })
            .catch((err) => {
                console.log(err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleResetSingleQuestionToDefault = (e: React.MouseEvent<HTMLDivElement>, index: number) => {
        e.stopPropagation();
        setMultipleChoiceExamQuestions(multipleChoiceExamQuestions.map((q, i) =>
            i === index ? multipleChoiceExamQuestionsDefault[i] : q
        ));
        setShowEditList(false);
        setSelectedIndex(null);
    }

    const handleResetAllToDefaults = () => {
        setMultipleChoiceExamQuestions(multipleChoiceExamQuestionsDefault);
    }

    const goToPreviousStep = () => {
        switch (step) {
            case 'MultipleChoiceExamDetail': {
                if (jobDetail?.recruitingInterview) {
                    setStep('RecruitingInterview');
                } else {
                    setStep('JobDetail');
                }
                break;
            }
            case 'CodingExamDetail': {
                if (jobDetail?.multipleChoiceExam) {
                    setStep('MultipleChoiceExamDetail');
                } else if (jobDetail?.recruitingInterview) {
                    setStep('RecruitingInterview');
                } else {
                    setStep('JobDetail');
                }
                break;
            }
            case 'ExamDeadline': {
                if (jobDetail?.codingExam) {
                    setStep('CodingExamDetail');
                } else if (jobDetail?.multipleChoiceExam) {
                    setStep('MultipleChoiceExamDetail');
                } else if (jobDetail?.recruitingInterview) {
                    setStep('RecruitingInterview');
                } else {
                    setStep('JobDetail');
                }
                break;
            }
            default:
                setStep('JobDetail');
                break;
        }
    }

    return (
        <div className={styles.container}>
            {isFetching && <Loading />}
            {step !== 'Approval' && <Stepper step={5} current={stepCount} />}
            {step === 'JobDetail' &&
                <JobDetail
                    city={city}
                    state={state}
                    states={states}
                    cities={cities}
                    setStep={setStep}
                    jobType={jobType}
                    setCity={setCity}
                    country={country}
                    setState={setState}
                    jobTitle={jobTitle}
                    countries={countries}
                    setJobType={setJobType}
                    setCountry={setCountry}
                    next={saveJobDetailStep}
                    setJobTitle={setJobTitle}
                    salaryAmount={salaryAmount}
                    workplaceType={workplaceType}
                    setCityAndState={setCityAndState}
                    setSalaryAmount={setSalaryAmount}
                    salaryFrequency={salaryFrequency}
                    setWorkplaceType={setWorkplaceType}
                    setSalaryFrequency={setSalaryFrequency}
                />}

            {step === 'RecruitingInterview' &&
                <RecruitingInterview
                    jobId={jobId}
                    setStep={setStep}
                    isLoading={isLoading}
                    resetToDefault={resetInterviewToDefaultNew}
                    recruitingInterview={takingRecruitingInterview}
                    defaultQuestions={defaultInterviewQuestions}
                    selectedQuestions={selectedInterviewQuestions}
                    setRecruitingInterview={setTakingRecruitingInterview}
                    setDefaultQuestions={setDefaultInterviewQuestions}
                    setSelectedQuestions={setSelectedInterviewQuestions}
                    submitFunction={postEditedRecruitingInterview}
                />}

            {step === 'MultipleChoiceExamDetail' &&
                <EditMultipleChoiceExamDetail
                    setShowEditList={setShowEditList}
                    setStep={setStep}
                    showEditList={showEditList}
                    isLoading={isLoading}
                    takingMultipleChoiceExam={takingMultipleChoiceExam}
                    setTakingMultipleChoiceExam={setTakingMultipleChoiceExam}
                    multipleChoiceExamQuestions={multipleChoiceExamQuestions}
                    selectedIndex={selectedIndex}
                    setSelectedIndex={setSelectedIndex}
                    setSelectedQuestion={setSelectedMultipleChoiceQuestion}
                    submitFunction={postEditedMultipleChoiceExam}
                    handleResetAllToDefaults={handleResetAllToDefaults}
                    goToPreviousStep={goToPreviousStep}
                    handleResetSingleQuestionToDefault={handleResetSingleQuestionToDefault}
                />}

            {step === 'CodingExamDetail' &&
                <EditCodingExamDetail
                    jobId={jobId}
                    setStep={setStep}
                    goToPreviousStep={goToPreviousStep}
                />}

            {step === 'ExamDeadline' &&
                <ExamDeadline
                    files={files}
                    setStep={setStep}
                    deadline={deadline}
                    fileSelect={fileSelect}
                    removeFile={removeFile}
                    setDeadline={setDeadline}
                    resumeMatching={resumeMatching}
                    setResumeMatching={setResumeMatching}
                    jobDescription={jobDescription}
                    setJobDescription={setJobDescription}
                    goToPreviousStep={goToPreviousStep}
                    fetchingJobDescription={fetchingJobDescription}
                    getJobDescriptionFromAI={getJobDescriptionFromAI}
                />}

            {(step === 'Review' || step === 'Approval') &&
                <Review
                    city={city}
                    state={state}
                    submit={submit}
                    setStep={setStep}
                    posting={posting}
                    jobType={jobType}
                    country={country}
                    deadline={deadline}
                    jobTitle={jobTitle}
                    isLoading={isLoading}
                    salaryAmount={salaryAmount}
                    workplaceType={workplaceType}
                    resumeMatching={resumeMatching}
                    jobDescription={jobDescription}
                    salaryFrequency={salaryFrequency}
                    codingExam={takingMultipleChoiceExam}
                    codingExamLanguages={codingExamLanguages}
                    multipleChoiceExam={takingMultipleChoiceExam}
                    selectedQuestions={selectedInterviewQuestions}
                    recruitingInterview={takingRecruitingInterview}
                    multipleChoiceCodingExam={multipleChoiceCodingExam}
                    multipleChoiceExamSkills={multipleChoiceExamSkills}
                    multipleChoiceCodingExamLanguages={multipleChoiceCodingExamLanguages}
                />}

            {editMultipleChoiceQuestionManuallyModal && <EditManually
                selectedQuestion={selectedMultipleChoiceQuestion}
                setMultipleChoiceExamQuestions={setMultipleChoiceExamQuestions}
            />}

            {regenerateMultipleChoiceQuestionByAIModal && <RegenerateByAI
                jobId={jobId}
                selectedIndex={selectedIndex}
                setSelectedIndex={setSelectedIndex}
                setMultipleChoiceExamQuestions={setMultipleChoiceExamQuestions}
            />}

        </div>
    )
};

export default EditJob;
